import React, { useState, useEffect } from 'react';
import { getFullApiUrl } from '../config';
import { getAuthHeaders, getUser } from '../utils/auth';

const AdminAreaContent = () => {
  const [activeTab, setActiveTab] = useState('users');
  
  // Loading states
  const [loading, setLoading] = useState({ users: true, groups: true, pages: true, permissions: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Data states
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [pages, setPages] = useState([]);
  const [userGroups, setUserGroups] = useState([]); // User-Group assignments
  const [pagePermissions, setPagePermissions] = useState({}); // { groupId: { pageId: permission } }
  
  // Modal states
  const [showUserModal, setShowUserModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [currentUser] = useState(getUser());

  // Form states
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    is_active: true,
    is_superuser: false,
    expired_date: null
  });

  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    is_active: true
  });

  // Load data on mount and when tab changes
  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
      loadGroups();
      loadUserGroups();
    } else if (activeTab === 'groups') {
      loadGroups();
    } else if (activeTab === 'pages') {
      // Load pages and groups first, then permissions will be loaded after
      loadPages();
      loadGroups();
    }
  }, [activeTab]);

  // Load page permissions after pages and groups are loaded
  useEffect(() => {
    if (activeTab === 'pages' && pages.length > 0 && groups.length > 0 && !loading.pages && !loading.groups) {
      loadPagePermissions();
    }
  }, [activeTab, pages.length, groups.length, loading.pages, loading.groups]);

  // Helper function to show messages
  const showMessage = (message, type = 'success') => {
    if (type === 'success') {
      setSuccess(message);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(message);
      setTimeout(() => setError(''), 5000);
    }
  };

  // ========== Load Data Functions ==========
  
  const loadUsers = async () => {
    try {
      setLoading(prev => ({ ...prev, users: true }));
      const response = await fetch(getFullApiUrl('USERS'), {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Gagal memuat data users');
      }
      
      const data = await response.json();
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
      showMessage(err.message || 'Terjadi kesalahan saat memuat data users', 'error');
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

  const loadGroups = async () => {
    try {
      setLoading(prev => ({ ...prev, groups: true }));
      const response = await fetch(getFullApiUrl('GROUPS_WITH_USERS'), {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Gagal memuat data groups');
      }
      
      const data = await response.json();
      setGroups(data || []);
    } catch (err) {
      console.error('Error loading groups:', err);
      showMessage(err.message || 'Terjadi kesalahan saat memuat data groups', 'error');
    } finally {
      setLoading(prev => ({ ...prev, groups: false }));
    }
  };

  const loadPages = async () => {
    try {
      setLoading(prev => ({ ...prev, pages: true }));
      const response = await fetch(getFullApiUrl('PAGES'), {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Gagal memuat data pages');
      }
      
      const data = await response.json();
      setPages(data || []);
    } catch (err) {
      console.error('Error loading pages:', err);
      showMessage(err.message || 'Terjadi kesalahan saat memuat data pages', 'error');
    } finally {
      setLoading(prev => ({ ...prev, pages: false }));
    }
  };

  const loadUserGroups = async () => {
    try {
      const response = await fetch(getFullApiUrl('USER_GROUPS'), {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserGroups(data || []);
      }
    } catch (err) {
      console.error('Error loading user groups:', err);
    }
  };

  const loadPagePermissions = async () => {
    try {
      setLoading(prev => ({ ...prev, permissions: true }));
      const response = await fetch(getFullApiUrl('PAGE_PERMISSIONS'), {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error('Gagal memuat page permissions');
      }
      
      const data = await response.json();
      // Transform to { groupId: { pageId: permission } }
      const permissionsMap = {};
      data.forEach(perm => {
        if (!permissionsMap[perm.group_id]) {
          permissionsMap[perm.group_id] = {};
        }
        permissionsMap[perm.group_id][perm.page_id] = perm;
      });
      
      // Ensure Admin group always has access to all pages
      const adminGroup = groups.find(g => g.name === "Admin" || g.name === "ADMIN");
      if (adminGroup && pages.length > 0) {
        if (!permissionsMap[adminGroup.id]) {
          permissionsMap[adminGroup.id] = {};
        }
        pages.forEach(page => {
          if (!permissionsMap[adminGroup.id][page.id]) {
            // Create virtual permission for Admin group (always has access)
            permissionsMap[adminGroup.id][page.id] = {
              id: null,
              group_id: adminGroup.id,
              page_id: page.id,
              can_view: true,
              can_create: true,
              can_edit: true,
              can_delete: true,
              is_active: true
            };
          } else {
            // Ensure Admin always has all permissions enabled
            permissionsMap[adminGroup.id][page.id].can_view = true;
            permissionsMap[adminGroup.id][page.id].can_create = true;
            permissionsMap[adminGroup.id][page.id].can_edit = true;
            permissionsMap[adminGroup.id][page.id].can_delete = true;
            permissionsMap[adminGroup.id][page.id].is_active = true;
          }
        });
      }
      
      setPagePermissions(permissionsMap);
    } catch (err) {
      console.error('Error loading page permissions:', err);
      showMessage(err.message || 'Terjadi kesalahan saat memuat page permissions', 'error');
    } finally {
      setLoading(prev => ({ ...prev, permissions: false }));
    }
  };

  // ========== User Management Functions ==========
  
  const handleCreateUser = () => {
    setEditingUser(null);
    setUserForm({
      username: '',
      email: '',
      full_name: '',
      password: '',
      is_active: true,
      is_superuser: false,
      expired_date: null
    });
    setShowUserModal(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username || '',
      email: user.email || '',
      full_name: user.full_name || '',
      password: '',
      is_active: user.is_active ?? true,
      is_superuser: user.is_superuser ?? false,
      expired_date: user.expired_date ? user.expired_date.split('T')[0] : null
    });
    setShowUserModal(true);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    try {
      const url = editingUser 
        ? `${getFullApiUrl('USERS')}/${editingUser.id}`
        : getFullApiUrl('USERS');
      
      const method = editingUser ? 'PUT' : 'POST';
      
      const body = { ...userForm };
      if (!editingUser && !body.password) {
        showMessage('Password harus diisi untuk user baru', 'error');
        return;
      }
      if (editingUser && !body.password) {
        delete body.password;
      }
      
      // Handle expired_date: set both expired_date and trial_end_date
      if (body.expired_date) {
        const expiredDateISO = new Date(body.expired_date).toISOString();
        body.expired_date = expiredDateISO;
        body.trial_end_date = expiredDateISO; // Set trial_end_date same as expired_date
      } else {
        body.expired_date = null;
        body.trial_end_date = null;
      }
      
      // trial_start_date will be set automatically by backend on create
      // Don't send it in the request

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Gagal menyimpan user');
      }

      showMessage(`User berhasil ${editingUser ? 'diupdate' : 'dibuat'}!`);
      setShowUserModal(false);
      loadUsers();
      loadUserGroups();
    } catch (err) {
      console.error('Error saving user:', err);
      showMessage(err.message || 'Terjadi kesalahan saat menyimpan user', 'error');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus user ini?')) {
      return;
    }

    try {
      const response = await fetch(`${getFullApiUrl('USERS')}/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Gagal menghapus user');
      }

      showMessage('User berhasil dihapus!');
      loadUsers();
      loadUserGroups();
    } catch (err) {
      console.error('Error deleting user:', err);
      showMessage(err.message || 'Terjadi kesalahan saat menghapus user', 'error');
    }
  };

  const handleUpdateUserExpiredDate = async (userId, newDate) => {
    try {
      const expiredDate = newDate ? new Date(newDate).toISOString() : null;
      const response = await fetch(`${getFullApiUrl('USERS')}/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          expired_date: expiredDate,
          trial_end_date: expiredDate // Also update trial_end_date
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Gagal mengupdate expired date');
      }

      showMessage('Expired date berhasil diupdate!');
      loadUsers();
    } catch (err) {
      console.error('Error updating expired date:', err);
      showMessage(err.message || 'Terjadi kesalahan saat mengupdate expired date', 'error');
    }
  };

  const handleAssignUserToGroup = async (userId, groupId) => {
    try {
      // Check if assignment already exists
      const existing = userGroups.find(
        ug => ug.user_id === userId && ug.group_id === groupId
      );

      if (existing) {
        // Update existing assignment
        const response = await fetch(`${getFullApiUrl('USER_GROUPS')}/${existing.id}`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ is_active: true })
        });

        if (!response.ok) throw new Error('Gagal mengupdate assignment');
      } else {
        // Create new assignment
        const response = await fetch(getFullApiUrl('USER_GROUPS'), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            user_id: userId,
            group_id: groupId,
            is_active: true,
            assigned_by: currentUser?.id
          })
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.detail || 'Gagal mengassign user ke group');
        }
      }

      showMessage('User berhasil diassign ke group!');
      loadUserGroups();
      loadUsers();
    } catch (err) {
      console.error('Error assigning user to group:', err);
      showMessage(err.message || 'Terjadi kesalahan saat mengassign user', 'error');
    }
  };

  // ========== Group Management Functions ==========
  
  const handleCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm({
      name: '',
      description: '',
      is_active: true
    });
    setShowGroupModal(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name || '',
      description: group.description || '',
      is_active: group.is_active ?? true
    });
    setShowGroupModal(true);
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    try {
      const url = editingGroup 
        ? `${getFullApiUrl('GROUPS')}/${editingGroup.id}`
        : getFullApiUrl('GROUPS');
      
      const method = editingGroup ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(groupForm)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Gagal menyimpan group');
      }

      showMessage(`Group berhasil ${editingGroup ? 'diupdate' : 'dibuat'}!`);
      setShowGroupModal(false);
      loadGroups();
      if (activeTab === 'pages') {
        loadPagePermissions();
      }
    } catch (err) {
      console.error('Error saving group:', err);
      showMessage(err.message || 'Terjadi kesalahan saat menyimpan group', 'error');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus group ini?')) {
      return;
    }

    try {
      const response = await fetch(`${getFullApiUrl('GROUPS')}/${groupId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Gagal menghapus group');
      }

      showMessage('Group berhasil dihapus!');
      loadGroups();
      if (activeTab === 'pages') {
        loadPagePermissions();
      }
    } catch (err) {
      console.error('Error deleting group:', err);
      showMessage(err.message || 'Terjadi kesalahan saat menghapus group', 'error');
    }
  };

  // ========== Page Permission Functions ==========
  
  const handlePagePermissionToggle = async (groupId, pageId) => {
    // Prevent editing Admin group permissions
    if (isAdminGroup(groupId)) {
      showMessage('Admin group selalu memiliki akses ke semua page dan tidak dapat diubah', 'error');
      return;
    }

    try {
      const currentPerm = pagePermissions[groupId]?.[pageId];
      const hasAccess = currentPerm?.can_view === true && currentPerm?.is_active === true;

      if (hasAccess) {
        // Remove permission (set is_active to false)
        if (currentPerm.id) {
          const response = await fetch(`${getFullApiUrl('PAGE_PERMISSIONS')}/${currentPerm.id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ is_active: false })
          });

          if (!response.ok) throw new Error('Gagal menghapus permission');
        }
      } else {
        // Add permission
        if (currentPerm?.id) {
          // Update existing but inactive permission
          const response = await fetch(`${getFullApiUrl('PAGE_PERMISSIONS')}/${currentPerm.id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify({ 
              is_active: true,
              can_view: true 
            })
          });

          if (!response.ok) throw new Error('Gagal mengupdate permission');
        } else {
          // Create new permission
          const response = await fetch(getFullApiUrl('PAGE_PERMISSIONS'), {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify({
              group_id: groupId,
              page_id: pageId,
              can_view: true,
              can_create: false,
              can_edit: false,
              can_delete: false,
              is_active: true
            })
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.detail || 'Gagal membuat permission');
          }
        }
      }

      showMessage('Page permission berhasil diupdate!');
      loadPagePermissions();
    } catch (err) {
      console.error('Error updating page permission:', err);
      showMessage(err.message || 'Terjadi kesalahan saat mengupdate permission', 'error');
    }
  };

  // ========== Helper Functions ==========
  
  const getUserGroups = (userId) => {
    return userGroups.filter(ug => ug.user_id === userId && ug.is_active);
  };

  const getUserGroupNames = (userId) => {
    const userGroupsData = getUserGroups(userId);
    return userGroupsData.map(ug => {
      const group = groups.find(g => g.id === ug.group_id);
      return group ? group.name : '';
    }).filter(Boolean);
  };

  const getUserPrimaryGroup = (userId) => {
    const userGroupsData = getUserGroups(userId);
    if (userGroupsData.length > 0) {
      const group = groups.find(g => g.id === userGroupsData[0].group_id);
      return group ? group.name : null;
    }
    return null;
  };

  const calculateDaysLeft = (expiredDate) => {
    if (!expiredDate) return null;
    const today = new Date();
    const expired = new Date(expiredDate);
    const diffTime = expired - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const hasPagePermission = (groupId, pageId) => {
    const perm = pagePermissions[groupId]?.[pageId];
    return perm?.can_view === true && perm?.is_active === true;
  };

  const isAdminGroup = (groupId) => {
    const group = groups.find(g => g.id === groupId);
    return group?.name === "Admin" || group?.name === "ADMIN";
  };

  return (
    <main className="p-6">
      <div className="w-full">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2 flex items-center transition-colors duration-300">
            <i className="fa-solid fa-shield-halved mr-3 text-blue-600 dark:text-blue-400"></i>
            Admin Area
          </h1>
          <p className="text-gray-600 dark:text-gray-300 text-sm transition-colors duration-300">
            Manage users, groups, and page permissions
          </p>
        </div>

        {/* Messages */}
        {success && (
          <div className="mb-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 px-4 py-3 rounded-lg flex items-center justify-between transition-colors duration-300">
            <span><i className="fa-solid fa-check-circle mr-2"></i>{success}</span>
            <button onClick={() => setSuccess('')} className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-200 transition-colors duration-200">
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center justify-between transition-colors duration-300">
            <span><i className="fa-solid fa-circle-exclamation mr-2"></i>{error}</span>
            <button onClick={() => setError('')} className="text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-200 transition-colors duration-200">
              <i className="fa-solid fa-times"></i>
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 mb-6 transition-colors duration-300">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'users'
                    ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <i className="fa-solid fa-users mr-2"></i>
                User Management
              </button>
              <button
                onClick={() => setActiveTab('groups')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'groups'
                    ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <i className="fa-solid fa-layer-group mr-2"></i>
                Group Management
              </button>
              <button
                onClick={() => setActiveTab('pages')}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'pages'
                    ? 'border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <i className="fa-solid fa-file mr-2"></i>
                Page Management
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
          {activeTab === 'users' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white transition-colors duration-300">User Management</h2>
                <button 
                  onClick={handleCreateUser}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add User
                </button>
              </div>
              
              {loading.users ? (
                <div className="text-center py-12">
                  <svg className="animate-spin h-8 w-8 text-blue-600 dark:text-blue-400 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">Memuat data users...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700 transition-colors duration-300">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Group</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Expired Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Trial Info</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Last Login</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 transition-colors duration-300">
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400 transition-colors duration-300">
                            Tidak ada data user
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => {
                          const daysLeft = calculateDaysLeft(user.expired_date);
                          const isExpired = daysLeft !== null && daysLeft < 0;
                          const primaryGroup = getUserPrimaryGroup(user.id);
                          const displayName = user.full_name || user.username || user.email?.split('@')[0] || 'User';
                          
                          return (
                            <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-8 w-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center transition-colors duration-300">
                                    <span className="text-blue-600 dark:text-blue-400 text-xs font-medium transition-colors duration-300">
                                      {displayName.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="ml-3">
                                    <div className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-300">{displayName}</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">{user.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full transition-colors duration-300 ${
                                  user.is_superuser 
                                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' 
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}>
                                  {user.is_superuser ? 'Superuser' : 'User'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <select
                                  value={primaryGroup || ''}
                                  onChange={(e) => {
                                    const selectedGroup = groups.find(g => g.name === e.target.value);
                                    if (selectedGroup) {
                                      handleAssignUserToGroup(user.id, selectedGroup.id);
                                    }
                                  }}
                                  className="text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                                >
                                  <option value="">- Pilih Group -</option>
                                  {groups.map(group => (
                                    <option key={group.id} value={group.name}>{group.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full transition-colors duration-300 ${
                                  user.is_active
                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                    : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                }`}>
                                  {user.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="date"
                                  value={user.expired_date ? user.expired_date.split('T')[0] : ''}
                                  onChange={(e) => handleUpdateUserExpiredDate(user.id, e.target.value)}
                                  className="text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300"
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {daysLeft !== null && (
                                  <div className="text-sm">
                                    {daysLeft > 0 ? (
                                      <span className="text-yellow-600 font-medium">
                                        <i className="fa-solid fa-clock mr-1"></i>
                                        {daysLeft} days left
                                      </span>
                                    ) : (
                                      <span className="text-red-600 font-medium">
                                        <i className="fa-solid fa-exclamation-triangle mr-1"></i>
                                        Expired {Math.abs(daysLeft)} days ago
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
                                {formatDateTime(user.last_login)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <button 
                                  onClick={() => handleEditUser(user)}
                                  className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mr-3 transition-colors duration-200" 
                                  title="Edit"
                                >
                                  <i className="fa-solid fa-edit"></i>
                                </button>
                                <button 
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200" 
                                  title="Delete"
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'groups' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white transition-colors duration-300">Group Management</h2>
                <button 
                  onClick={handleCreateGroup}
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg text-sm transition-colors duration-200"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add Group
                </button>
              </div>
              
              {loading.groups ? (
                <div className="text-center py-12">
                  <svg className="animate-spin h-8 w-8 text-blue-600 dark:text-blue-400 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-gray-600 dark:text-gray-400 transition-colors duration-300">Memuat data groups...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groups.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500 dark:text-gray-400 transition-colors duration-300">
                      Tidak ada data group
                    </div>
                  ) : (
                    groups.map((group) => (
                      <div key={group.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 transition-colors duration-300">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-semibold text-gray-800 dark:text-white transition-colors duration-300">{group.name}</h3>
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleEditGroup(group)}
                              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200"
                            >
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button 
                              onClick={() => handleDeleteGroup(group.id)}
                              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 transition-colors duration-300">{group.description || '-'}</p>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-4 transition-colors duration-300">
                          <i className="fa-solid fa-users mr-1"></i>
                          {group.user_count || 0} users
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full transition-colors duration-300 ${
                          group.is_active
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        }`}>
                          {group.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'pages' && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2 transition-colors duration-300">Page Management</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-300">
                  Configure which groups can access which pages
                </p>
              </div>
              
              {(loading.pages || loading.groups || loading.permissions) ? (
                <div className="text-center py-12">
                  <svg className="animate-spin h-8 w-8 text-blue-600 dark:text-blue-400 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <p className="text-gray-600 dark:text-gray-400 transition-colors duration-300">Memuat data pages dan permissions...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700 transition-colors duration-300">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">Page</th>
                        {groups.map((group) => (
                          <th key={group.id} className="px-6 py-3 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase transition-colors duration-300">
                            {group.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {pages.length === 0 ? (
                        <tr>
                          <td colSpan={groups.length + 1} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400 transition-colors duration-300">
                            Tidak ada data pages
                          </td>
                        </tr>
                      ) : (
                        pages.map((page) => (
                          <tr key={page.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-300">{page.name}</div>
                              {page.slug && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">{page.slug}</div>
                              )}
                            </td>
                            {groups.map((group) => {
                              const hasAccess = hasPagePermission(group.id, page.id);
                              const isAdmin = isAdminGroup(group.id);
                              return (
                                <td key={group.id} className="px-6 py-4 whitespace-nowrap text-center">
                                  {isAdmin ? (
                                    <div className="flex items-center justify-center" title="Admin group selalu memiliki akses penuh">
                                      <div className="w-11 h-6 bg-blue-600 dark:bg-blue-500 rounded-full relative cursor-not-allowed opacity-75 transition-colors duration-300">
                                        <div className="absolute top-[2px] right-[2px] bg-white border border-gray-300 dark:border-gray-600 rounded-full h-5 w-5 transition-colors duration-300"></div>
                                      </div>
                                      <i className="fa-solid fa-lock ml-2 text-gray-400 dark:text-gray-500 text-xs transition-colors duration-300" title="Tidak dapat diubah"></i>
                                    </div>
                                  ) : (
                                    <label className="relative inline-flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={hasAccess}
                                        onChange={() => handlePagePermissionToggle(group.id, page.id)}
                                        className="sr-only peer"
                                      />
                                      <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500 transition-colors duration-300"></div>
                                    </label>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingUser ? 'Edit User' : 'Add User'}
                </h3>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
              
              <form onSubmit={handleSaveUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username *
                    </label>
                    <input
                      type="text"
                      value={userForm.username}
                      onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={userForm.email}
                      onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={userForm.full_name}
                    onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password {!editingUser && '*'}
                  </label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required={!editingUser}
                    minLength={6}
                    placeholder={editingUser ? "Kosongkan jika tidak ingin mengubah" : ""}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expired Date
                    </label>
                    <input
                      type="date"
                      value={userForm.expired_date || ''}
                      onChange={(e) => setUserForm({ ...userForm, expired_date: e.target.value || null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={userForm.is_active}
                      onChange={(e) => setUserForm({ ...userForm, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={userForm.is_superuser}
                      onChange={(e) => setUserForm({ ...userForm, is_superuser: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Superuser</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowUserModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    {editingUser ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingGroup ? 'Edit Group' : 'Add Group'}
                </h3>
                <button
                  onClick={() => setShowGroupModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
              
              <form onSubmit={handleSaveGroup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows="3"
                  />
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={groupForm.is_active}
                      onChange={(e) => setGroupForm({ ...groupForm, is_active: e.target.checked })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowGroupModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    {editingGroup ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminAreaContent;
